import React from 'react';

import { TwirpError } from '@src/gen/twirp';
import { Error403Page, Error404Page, Error500Page, OopsPage } from '@shared-legacy/app/pages/ErrorPage';
import { GrpcStatusCode, isGrpcGatewayErrorResponse, TwirpErrorCode } from '@shared/services/appstore/HTTPResponse';

import { RenderErrorInsufficientPermissions, RenderErrorNotFound, RenderErrorServerException } from './renderErrors';

export type ErrorBoundaryProps = {
  children: React.ReactNode;
};

type ErrorBoundaryState = {
  captured: { error: unknown } | undefined;
};

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { captured: undefined };
  }

  static getDerivedStateFromError(error: unknown) {
    return { captured: { error } };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    console.error(`ErrorBoundary caught an error: ${error}`, errorInfo);
  }

  render() {
    if (!this.state.captured) {
      return this.props.children;
    }
    const { error } = this.state.captured;
    const grpcGatewayError = isGrpcGatewayErrorResponse(error);
    const twirpError = error instanceof TwirpError;
    if (
      error instanceof RenderErrorInsufficientPermissions ||
      (grpcGatewayError &&
        (error.code === GrpcStatusCode.PERMISSION_DENIED || error.code === GrpcStatusCode.UNAUTHENTICATED)) ||
      (twirpError && (error.code === TwirpErrorCode.PermissionDenied || error.code === TwirpErrorCode.Unauthenticated))
    ) {
      return <Error403Page />;
    }
    if (
      error instanceof RenderErrorNotFound ||
      (grpcGatewayError && error.code === GrpcStatusCode.NOT_FOUND) ||
      (twirpError && error.code === TwirpErrorCode.NotFound)
    ) {
      return <Error404Page />;
    }
    if (
      error instanceof RenderErrorServerException ||
      (grpcGatewayError && error.code === GrpcStatusCode.INTERNAL) ||
      (twirpError && error.code === TwirpErrorCode.Internal)
    ) {
      return <Error500Page />;
    }
    return <OopsPage />;
  }
}
